// OfferHero — large featured presentation of a single offer.
//
// Used on:
//   - The Rewards page top (next-uncredited offer with artwork)
//   - The Home dashboard (today's challenge / most-relevant offer)
//
// Renders ONLY when the offer has image_url. Caller is responsible
// for picking which offer to feature and for rendering a fallback
// (e.g. the original text-only banner) when no eligible offer exists.
//
// Layout:
//   - Mobile (default):  artwork floats absolute top-right (~140px),
//     breaking out of the rounded boundary by ~20px above. Text
//     content fills full width with right padding to clear the
//     image. Stacks naturally because artwork is positioned, not
//     in flow.
//   - sm and up:         artwork grows to ~220-260px, text padding
//     scales with it. Still side-by-side via the same absolute
//     positioning — predictable layout, no flex-row gymnastics.
//
// Background:
//   Two-layer dark gradient (deep arena tones) overlaid with two
//   soft radial halos (brand orange top-right behind the artwork,
//   electric cyan bottom-left for ambient color). High contrast so
//   the transparent PNG reads as floating ABOVE the surface.

import type { OfferLike, ClaimLike } from './OfferCard';
import { Link } from 'react-router-dom';
import Icon from './Icon';
import { useT } from '../lib/i18n';

export default function OfferHero({
  offer,
  onClaim,
  busy = false,
  eyebrow,
}: {
  offer: OfferLike;
  onClaim?: (offerId: number) => void | Promise<void>;
  busy?: boolean;
  /** Small uppercase label above the title. e.g. "Featured Reward",
   *  "Today's challenge". Caller supplies it (i18n stays in caller). */
  eyebrow: string;
}) {
  const t = useT();
  const claim: ClaimLike | null = offer.my_latest_claim;

  const rewardText = offer.reward_kind === 'percent'
    ? `${offer.reward_value}%${offer.reward_cap ? ` (${t('rewards.cap')} ₹${offer.reward_cap.toLocaleString('en-IN')})` : ''}`
    : `₹${offer.reward_value.toLocaleString('en-IN')}`;

  const lifecycleBits: string[] = [];
  if (offer.ends_at) {
    lifecycleBits.push(t('rewards.expires', { date: new Date(offer.ends_at).toLocaleDateString() }));
  }
  if (offer.global_budget != null) {
    const remaining = Math.max(0, offer.global_budget - (offer.budget_used || 0));
    lifecycleBits.push(t('rewards.remaining', { amount: '₹' + remaining.toLocaleString('en-IN') }));
  }

  // CTA state machine — mirrors OfferCard's ActionLine but with a
  // chunkier CTA suited to the hero scale.
  // Phase 16.11: also check rejected so we don't render a Claim
  // button the engine would refuse.
  // Phase 16.12: when no claim, branch on cta.mode:
  //   'navigate' → render a Link button to cta.target with cta.label.
  //                Doesn't require onClaim (it's just navigation).
  //   'claim'    → render the Claim button (requires onClaim).
  const isCredited = claim?.state === 'credited';
  const isPending  = claim?.state === 'pending' || claim?.state === 'approved';
  const isRejected = claim?.state === 'rejected';
  const noClaimYet = !isCredited && !isPending && !isRejected;
  const cta        = offer.cta;
  // Navigate CTA: needs the cta payload + a target. Doesn't depend on
  // onClaim because tapping just navigates.
  const useNav     = noClaimYet && cta?.mode === 'navigate' && !!cta.target;
  // Claim CTA: needs onClaim AND we're not in navigate mode.
  const canClaim   = noClaimYet && !useNav && !!onClaim;

  return (
    // Phase R7 follow-up (2026-05-13): outer rounding pinned at
    // `rounded-3xl` to match (a) the inner gradient wrapper below
    // — which is also `rounded-3xl` — so the gradient's rounded
    // edge sits flush with the section's border, with no sliver of
    // "empty" frame visible at the corners, and (b) the
    // RewardsTeaserStrip card on the logged-out homepage, which
    // already uses `rounded-3xl`.
    <section
      className="relative overflow-visible rounded-3xl
                 border border-arena-200/60 dark:border-white/10
                 shadow-[0_20px_60px_-25px_rgba(0,0,0,0.45)]
                 dark:shadow-[0_20px_60px_-25px_rgba(0,0,0,0.65)]
                 transition-transform duration-200"
    >
      {/* ─── Background layers (clipped to the rounded boundary) ───
          Phase R5+ visual fix (2026-05-10): the previous Task F
          refactor used a cyan→amber LIGHT gradient that read as
          two distinct colour "sheets" pasted together (per user
          feedback) AND the dark-only text colours stayed white,
          which made the title invisible on the light wash.

          New light gradient: a single warm cream→peach→brand-orange
          ramp — one cohesive surface, no cyan band. Dark gradient:
          unchanged (the existing 3D artwork was designed against
          the deep navy/brown ramp). */}
      <div className="absolute inset-0 rounded-3xl overflow-hidden pointer-events-none" aria-hidden="true">
        {/* Light-mode base — Phase R7 follow-up (2026-05-13):
            switched from the all-orange cream→brand-orange ramp to
            the cyan-ocean → amber wash that the LOGGED-OUT
            RewardsTeaserStrip uses. Identical gradient stops so
            the logged-in offer hero now visually echoes the
            anonymous-visitor rewards teaser. */}
        <div className="block dark:hidden absolute inset-0"
             style={{
               background:
                 'linear-gradient(135deg, #E0F7FA 0%, #B3E9FF 40%, #FFD9A8 80%, #F4A24A 100%)',
             }} />
        {/* Dark-mode base — preserved the original deep luxury
            ramp; existing offer artwork was designed against it.
            UNCHANGED — user explicitly wants dark mode left alone. */}
        <div className="hidden dark:block absolute inset-0 bg-gradient-to-br
                        from-[#0a1626] via-[#0e1d33] to-[#1a0f08]" />
        {/* Ambient halos — brand-orange top-right + cyan bottom-left,
            mix-blend-screen so they lift against any base. Mirrors
            RewardsTeaserStrip exactly. Slightly stronger in dark
            (40 / 30) than light (30 / 25) so dark keeps its
            "luxury back-light" punch and light doesn't over-saturate. */}
        <div className="block dark:hidden absolute -top-1/3 -right-1/4 w-3/4 h-3/4
                        bg-brand-500/30 rounded-full blur-3xl mix-blend-screen" />
        <div className="hidden dark:block absolute -top-1/3 -right-1/4 w-3/4 h-3/4
                        bg-brand-500/40 rounded-full blur-3xl mix-blend-screen" />
        <div className="block dark:hidden absolute -bottom-1/3 -left-1/4 w-2/3 h-2/3
                        bg-cyan-500/25 rounded-full blur-3xl mix-blend-screen" />
        <div className="hidden dark:block absolute -bottom-1/3 -left-1/4 w-2/3 h-2/3
                        bg-cyan-500/30 rounded-full blur-3xl mix-blend-screen" />
        {/* Subtle grain / vignette via inset shadow — keeps the
            gradient from looking flat. */}
        <div className="absolute inset-0 shadow-[inset_0_2px_30px_rgba(255,255,255,0.06),inset_0_-40px_60px_rgba(0,0,0,0.18)]
                        dark:shadow-[inset_0_2px_30px_rgba(255,255,255,0.04),inset_0_-40px_60px_rgba(0,0,0,0.45)]" />
      </div>

      {/* ─── Artwork tile (absolute, breaks out of the card edge) ───
          Phase 16.5: cyan + brand-orange halos that used to sit behind
          this image were removed — they read as fake light. The
          page-level ambient backdrop above (lines ~82/86) stays
          because it washes the entire hero, not just the artwork.
          The artwork itself now gets a single small black drop-shadow:
          4px down, 6px blur, 20% opacity.
          Phase R7 follow-up (2026-05-13): artwork shrunk ~30% per
          breakpoint so the hero takes far less viewport. The break-
          out offsets are scaled with it. */}
      <div
        className="pointer-events-none absolute z-20
                   -top-4 -right-2 w-24 h-24
                   sm:-top-5 sm:-right-4 sm:w-36 sm:h-36
                   lg:-top-6 lg:-right-5 lg:w-44 lg:h-44"
        aria-hidden="true"
      >
        <img
          src={offer.image_url!}
          alt=""
          loading="lazy"
          decoding="async"
          className="relative w-full h-full object-contain
                     drop-shadow-[0_4px_6px_rgba(0,0,0,0.20)]"
        />
      </div>

      {/* ─── Content ───
          Phase R7 follow-up (2026-05-13): padding + right-padding +
          min-height all reduced to match the smaller artwork. The
          space-y values stay (vertical rhythm between eyebrow /
          title / reward / desc / lifecycle / CTA reads the same
          visually, just at a smaller overall scale). */}
      <div className="relative z-10
                      p-4 sm:p-5 lg:p-6
                      pr-24 sm:pr-40 lg:pr-48
                      space-y-2 sm:space-y-2.5
                      min-h-[96px] sm:min-h-[136px] lg:min-h-[160px]
                      flex flex-col justify-center">
        {/* Eyebrow — Phase R5+ visual fix: was `text-brand-300/95`
            (a pale orange) which read OK on the dark gradient but
            disappeared on the new warm-orange light gradient. Now
            paired: deep brand-700 in light, original brand-300 in
            dark. */}
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[.28em]
                        font-extrabold text-brand-800 dark:text-brand-300/95">
          {eyebrow}
        </div>

        {/* Title — Phase R5+ visual fix: was `text-white` only,
            which made the offer title invisible on the warm light
            gradient. Now: dark navy in light (max contrast on the
            cream/orange wash), white-with-shadow in dark.
            Phase R7 follow-up (2026-05-13): title size reduced one
            tailwind step at every breakpoint. */}
        <h2 className="font-display font-black
                       text-lg sm:text-2xl lg:text-3xl
                       leading-[1.05] line-clamp-2
                       text-arena-900 dark:text-white
                       dark:drop-shadow-[0_2px_8px_rgba(0,0,0,0.5)]">
          {offer.title}
        </h2>

        {/* Reward amount — same fix: deep brand-800 (almost black-
            orange) in light, original brand-300 in dark.
            Phase R7 follow-up (2026-05-13): reward size reduced one
            tailwind step at every breakpoint to track the title. */}
        <div className="font-display font-black
                        text-sm sm:text-xl lg:text-2xl
                        tabular-nums leading-none
                        text-brand-800 dark:text-brand-300
                        dark:drop-shadow-[0_2px_8px_rgba(var(--brand-rgb),0.4)]">
          {rewardText}
        </div>

        {/* Description (only on sm+ to keep mobile tight) — was
            `text-white/75` only (invisible in light). Now paired. */}
        {offer.description && (
          <p className="hidden sm:block text-sm line-clamp-2 max-w-md
                        text-arena-800 dark:text-white/75">
            {offer.description}
          </p>
        )}

        {/* Lifecycle metadata — was `text-white/60` only (invisible
            in light). Now paired. */}
        {lifecycleBits.length > 0 && (
          <div className="text-[10px] sm:text-[11px] uppercase tracking-[.18em] truncate
                          text-arena-700 dark:text-white/60">
            {lifecycleBits.join('  ·  ')}
          </div>
        )}

        {/* CTA / state — status badges were `text-emerald-200` /
            `text-amber-200` / `text-rose-200` only, which were
            invisible on the light warm gradient. Each now has a
            paired darker tone for light mode. */}
        <div className="pt-1.5 sm:pt-2">
          {isCredited && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                            bg-emerald-500/25 border border-emerald-600/50
                            text-emerald-900 dark:text-emerald-200 text-xs font-bold
                            dark:bg-emerald-400/20 dark:border-emerald-400/40">
              <Icon name="check_circle" size={14} filled />
              {t('rewards.claimed.credited', {
                amount: '₹' + (claim!.computed_amount).toLocaleString('en-IN'),
                date: new Date(claim!.created_at).toLocaleDateString(),
              })}
            </div>
          )}
          {isPending && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                            bg-amber-500/25 border border-amber-600/50
                            text-amber-900 dark:text-amber-200 text-xs font-bold
                            dark:bg-amber-400/15 dark:border-amber-400/40">
              <Icon name="pending" size={14} filled />
              {t('rewards.claimed.pending')}
            </div>
          )}
          {/* Phase 16.11: rejected indicator on the hero scale.
              Same precedence as OfferCard — show why, no retry. */}
          {isRejected && (
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full
                            bg-rose-500/25 border border-rose-600/50
                            text-rose-900 dark:text-rose-200 text-xs font-bold
                            dark:bg-rose-400/15 dark:border-rose-400/40">
              <Icon name="cancel" size={14} filled />
              {claim?.decision_note ? `"${claim.decision_note}"` : t('rewards.claimed.rejected')}
            </div>
          )}
          {/* Phase 16.12: navigate-mode CTA.
              Same chunky brand-orange treatment as the Claim button so
              the hero theme stays consistent — only the action differs.
              Player taps → routed to the screen where the offer is
              earned (recharge page, specific game, games list); the
              engine auto-credits once they meet the criteria. */}
          {useNav && (
            <Link
              to={cta!.target!}
              className="inline-flex items-center justify-center whitespace-nowrap gap-1.5
                         h-10 sm:h-12 px-5 sm:px-7 rounded-full
                         text-sm sm:text-base font-extrabold uppercase tracking-wider text-white
                         bg-gradient-to-b from-brand-400 via-brand-500 to-brand-600
                         border border-white/20 active:scale-[0.98] transition"
              style={{
                boxShadow:
                  '0 12px 28px -10px rgba(var(--brand-rgb),0.7), inset 0 1px 0 rgba(255,255,255,0.35)',
              }}
            >
              {cta!.label} <Icon name="arrow_forward" size={18} filled />
            </Link>
          )}
          {canClaim && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onClaim?.(offer.id)}
              className="inline-flex items-center justify-center whitespace-nowrap gap-1.5
                         h-10 sm:h-12 px-5 sm:px-7 rounded-full
                         text-sm sm:text-base font-extrabold uppercase tracking-wider text-white
                         bg-gradient-to-b from-brand-400 via-brand-500 to-brand-600
                         border border-white/20 active:scale-[0.98] transition disabled:opacity-50"
              style={{
                boxShadow:
                  '0 12px 28px -10px rgba(var(--brand-rgb),0.7), inset 0 1px 0 rgba(255,255,255,0.35)',
              }}
            >
              {busy ? (
                <><Icon name="progress_activity" size={18} className="animate-spin" /> {t('common.loading')}</>
              ) : offer.approval === 'auto' ? (
                <>{t('rewards.claim.cta')} {rewardText} <Icon name="arrow_forward" size={18} filled /></>
              ) : (
                <>{t('rewards.claim.submit')} <Icon name="arrow_forward" size={18} filled /></>
              )}
            </button>
          )}
          {noClaimYet && !useNav && !canClaim && (
            // Pure-claim type, no onClaim callback supplied (e.g. the
            // Home page hero where claiming requires going to the
            // Rewards page first). Phase 16.13: route to /rewards
            // instead of the passive caption — ensures every hero
            // card has a working CTA.
            <Link
              to="/rewards"
              className="inline-flex items-center justify-center whitespace-nowrap gap-1.5
                         h-10 sm:h-12 px-5 sm:px-7 rounded-full
                         text-sm sm:text-base font-extrabold uppercase tracking-wider text-white
                         bg-gradient-to-b from-brand-400 via-brand-500 to-brand-600
                         border border-white/20 active:scale-[0.98] transition"
              style={{
                boxShadow:
                  '0 12px 28px -10px rgba(var(--brand-rgb),0.7), inset 0 1px 0 rgba(255,255,255,0.35)',
              }}
            >
              {cta?.label || 'View reward'} <Icon name="arrow_forward" size={18} filled />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

/**
 * Pick the best offer to feature in the hero from a list of visible
 * offers. Filters out offers without artwork (the hero requires
 * imagery), then ranks by:
 *   1) actionable first (uncredited & not pending)
 *   2) soonest ends_at next (urgency drives action)
 *   3) highest reward_value as tiebreak
 *
 * Returns null when no offer with artwork is eligible — caller should
 * render its fallback (text banner, nothing, etc).
 */
export function pickHeroOffer(offers: OfferLike[]): OfferLike | null {
  const withArt = offers.filter((o) => o.image_url);
  if (!withArt.length) return null;

  const ranked = [...withArt].sort((a, b) => {
    const aActive = !(a.my_latest_claim?.state === 'credited' || a.my_latest_claim?.state === 'pending' || a.my_latest_claim?.state === 'approved');
    const bActive = !(b.my_latest_claim?.state === 'credited' || b.my_latest_claim?.state === 'pending' || b.my_latest_claim?.state === 'approved');
    if (aActive !== bActive) return aActive ? -1 : 1;

    const aEnds = a.ends_at ? new Date(a.ends_at).getTime() : Number.POSITIVE_INFINITY;
    const bEnds = b.ends_at ? new Date(b.ends_at).getTime() : Number.POSITIVE_INFINITY;
    if (aEnds !== bEnds) return aEnds - bEnds;

    return (b.reward_value || 0) - (a.reward_value || 0);
  });

  return ranked[0];
}
